import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import StorageIcon from '@mui/icons-material/Storage';
import BarChartIcon from '@mui/icons-material/BarChart';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import EmailIcon from '@mui/icons-material/Email';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import TuneIcon from '@mui/icons-material/Tune';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';

export const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const MAIN_NAV: NavItem[] = [
  { label: 'Overview', path: '/admin-overview', icon: <AdminPanelSettingsIcon fontSize="small" /> },
  { label: 'Sites', path: '/all-sites', icon: <StorageIcon fontSize="small" /> },
  { label: 'Managed Web', path: '/managed-web-presence', icon: <LanguageIcon fontSize="small" /> },
  { label: 'Work Board', path: '/work-board', icon: <ViewKanbanIcon fontSize="small" /> },
  { label: 'Orchestrator', path: '/orchestrator', icon: <ScheduleIcon fontSize="small" /> },
  { label: 'Analytics', path: '/analytics', icon: <BarChartIcon fontSize="small" /> },
];

const CONFIG_NAV: NavItem[] = [
  { label: 'Triage Config', path: '/triage-config', icon: <TuneIcon fontSize="small" /> },
  { label: 'Campaigns', path: '/campaigns', icon: <EmailIcon fontSize="small" /> },
  { label: 'Design Sessions', path: '/design-sessions', icon: <DesignServicesIcon fontSize="small" /> },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'RBAC', path: '/rbac', icon: <SecurityIcon fontSize="small" /> },
  { label: 'Audit Log', path: '/audit-log', icon: <HistoryIcon fontSize="small" /> },
  { label: 'Impersonation', path: '/impersonation', icon: <SwitchAccountIcon fontSize="small" /> },
];

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) onMobileClose();
  };

  const renderNavItem = (item: NavItem) => {
    const isSelected = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    return (
      <ListItemButton
        key={item.path}
        selected={isSelected}
        onClick={() => handleNav(item.path)}
        sx={{
          borderRadius: '6px',
          mb: 0.25,
          minHeight: 36,
          py: 0.5,
          px: 1.5,
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: '#fff',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
            '& .MuiListItemIcon-root': { color: '#5eead4' },
          },
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.05)',
          },
          color: 'rgba(255,255,255,0.6)',
          '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.4)' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontWeight: 500, fontSize: '0.8rem' }}
        />
      </ListItemButton>
    );
  };

  const sectionLabel = (label: string) => (
    <Typography
      variant="overline"
      sx={{
        px: 1.5,
        pt: 2,
        pb: 0.5,
        display: 'block',
        color: 'rgba(255,255,255,0.3)',
        fontSize: '0.6rem',
        letterSpacing: '0.1em',
      }}
    >
      {label}
    </Typography>
  );

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#0f172a',
        color: '#fff',
      }}
    >
      {/* Logo */}
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #0d9488, #5eead4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#0f172a' }}>U</Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.9rem',
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
              color: '#fff',
            }}
          >
            UITL
          </Typography>
          <Typography
            sx={{
              fontSize: '0.65rem',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '0.02em',
            }}
          >
            Mission Control
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 1, flexGrow: 1 }}>
        <ListItemButton
          selected={location.pathname === '/my-sites' || location.pathname.startsWith('/my-sites/')}
          onClick={() => handleNav('/my-sites')}
          sx={{
            borderRadius: '6px',
            mb: 1.5,
            minHeight: 36,
            py: 0.5,
            px: 1.5,
            '&.Mui-selected': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
              '& .MuiListItemIcon-root': { color: '#5eead4' },
            },
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
            color: 'rgba(255,255,255,0.6)',
            '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.4)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}><LanguageIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="My Sites" primaryTypographyProps={{ fontWeight: 500, fontSize: '0.8rem' }} />
        </ListItemButton>

        {sectionLabel('Platform')}
        {MAIN_NAV.map(renderNavItem)}

        {sectionLabel('Configure')}
        {CONFIG_NAV.map(renderNavItem)}

        {sectionLabel('Access')}
        {ADMIN_NAV.map(renderNavItem)}
      </List>

      {/* Bottom */}
      <List sx={{ px: 1, pb: 1.5 }}>
        <ListItemButton
          selected={location.pathname === '/settings'}
          onClick={() => handleNav('/settings')}
          sx={{
            borderRadius: '6px',
            minHeight: 36,
            py: 0.5,
            px: 1.5,
            '&.Mui-selected': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
              '& .MuiListItemIcon-root': { color: '#5eead4' },
            },
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
            color: 'rgba(255,255,255,0.4)',
            '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.35)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Settings" primaryTypographyProps={{ fontWeight: 500, fontSize: '0.8rem' }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            border: 'none',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
